import classNames from 'classnames';
import React, { useMemo } from 'react';
import styles from './index.module.less';
import { CSSProperties, forwardRef } from 'react';

export interface BaseIconProps {
	icon: React.ReactNode;
	size?: number;
	color?: string;
	fillColor?: string;
	strokeColor?: string;
	style?: CSSProperties;
	className?: string;
	onClick?: React.MouseEventHandler<HTMLDivElement>;
}

// 生成唯一 ID 的工具函数
const generateUniqueId = () => {
	return `svg_${Math.random().toString(36).slice(2, 11)}_${Date.now()}`;
};

// 为 SVG 中的 ID 生成唯一标识符，避免多个 SVG 实例之间的 ID 冲突
export const makeSvgIdsUnique = (svgString: string): string => {
	if (typeof svgString !== 'string') return svgString;

	// 生成唯一前缀
	const uniquePrefix = generateUniqueId();

	// 匹配所有 id 属性
	const idRegex = /id="([^"]+)"/g;
	const idMap = new Map<string, string>();

	// 收集所有 ID
	let match;
	while ((match = idRegex.exec(svgString)) !== null) {
		const originalId = match[1];
		if (!idMap.has(originalId)) {
			idMap.set(originalId, `${originalId}_${uniquePrefix}`);
		}
	}

	// 如果没有找到任何 ID，直接返回原字符串
	if (idMap.size === 0) return svgString;

	let result = svgString;

	// 替换所有 id 定义
	idMap.forEach((newId, oldId) => {
		result = result.replace(new RegExp(`id="${oldId}"`, 'g'), `id="${newId}"`);
	});

	// 替换所有引用（url(#id) 格式）
	idMap.forEach((newId, oldId) => {
		const urlRegex = new RegExp(`url\\(#${oldId}\\)`, 'g');
		result = result.replace(urlRegex, `url(#${newId})`);
	});

	return result;
};

const applySvgColorOverrides = (
	svgString: string,
	{ fillColor, strokeColor }: Pick<BaseIconProps, 'fillColor' | 'strokeColor'>,
) => {
	let result = svgString;

	if (fillColor) {
		result = result.replace(/fill="currentColor"/g, `fill="${fillColor}"`);
	}

	if (strokeColor) {
		result = result.replace(/stroke="white"/g, `stroke="${strokeColor}"`);
	}

	return result;
};

export const NewBaseIcon = forwardRef<HTMLDivElement, BaseIconProps>((props, ref) => {
	const { icon, size = 16, color, fillColor, strokeColor, style, className, ...restProps } = props;

	const computedStyle = { width: size, height: size, color: color, ...style };

	// 为 SVG 字符串生成唯一的 ID，避免多个实例之间的冲突
	const uniqueIcon = useMemo(() => {
		if (typeof icon === 'string') {
			return applySvgColorOverrides(makeSvgIdsUnique(icon), { fillColor, strokeColor });
		}
		return icon;
	}, [fillColor, icon, strokeColor]);

	return typeof icon === 'string' ? (
		<div
			style={computedStyle}
			ref={ref}
			className={classNames([styles.icon_style], [className])}
			dangerouslySetInnerHTML={{ __html: uniqueIcon as string }}
			{...restProps}
		></div>
	) : (
		icon
	);
});
